import { v4 as uuidv4 } from 'uuid';
import * as ClientJs from 'clientjs';

const utils = {
  /**
   * 📌 Note: This custom implementation is used instead of lodash's `isEmpty` function
   *       due to a known issue with lodash's handling of numbers, false, true values.
   *       Refer to https://github.com/lodash/lodash/issues/496 for more information.
   *
   * The function checks if a value is considered empty, handling various data types.
   * @param value - The value parameter is a variable that can hold any data type (undefined, null,
   * object, string, number, boolean, etc.) and is used to check if it is empty or not.
   * @returns The function `isValueEmpty` takes in a parameter `value` and returns a boolean value. It
   * returns `true` if the value is `undefined`, `null`, an empty object, or a string with only
   * whitespace characters. Otherwise, it returns `false`.
   */
  isValueEmpty: (value) => {
    const condition =
      value === undefined ||
      value === null ||
      (typeof value === 'object' && Object.keys(value).length === 0);
    return (
      condition || (typeof value === 'string' && value.trim().length === 0)
    );
  },

  applyThemeToMonacoEditor: (monaco, theme, userTheme) => {
    if (!monaco || !theme) return;

    const isDark = userTheme === 'dark';
    const themeName = isDark ? 'myThemeDark' : 'myThemeLight';

    const background = isDark
      ? theme?.palette?.background?.paper
      : theme?.palette?.other?.white;

    const foreground = isDark
      ? theme?.palette?.other?.white
      : theme?.palette?.other?.black;

    const lineNumberColor = theme.palette.primary.main;
    const gutterBackground = isDark
      ? theme?.palette?.other?.sidebarMenuSelected
      : '#D6E6FD';

    monaco.editor.defineTheme(themeName, {
      base: isDark ? 'vs-dark' : 'vs',
      inherit: true,
      rules: [
        { token: 'comment', foreground: 'ffa500' },
        { token: 'keyword', foreground: '00ff00' },
        { token: 'string', foreground: 'ff4d4d' },
        { token: 'identifier', foreground: isDark ? 'ffffff' : '000000' },
        { token: 'number', foreground: isDark ? '7cd3fc' : '147aaf' },
      ],
      colors: {
        'editor.foreground': foreground,
        'editor.background': background,
        'editorGutter.background': gutterBackground,
        'editorLineNumber.foreground': lineNumberColor,

        'dropdown.border': theme?.palette?.other?.black,
        'editorLineNumber.activeForeground': theme?.palette?.other?.black,
        'editorGutter.foldingControlForeground': theme?.palette?.other?.black,
        'scrollbarSlider.background': '#D6E6FD',
        'scrollbarSlider.activeBackground': '#D6E6FD',
        'scrollbarSlider.hoverBackground': '#D6E6FD',
      },
    });

    monaco.editor.setTheme(themeName);
  },

  caseInsensitiveSort: (rowA, rowB, columnId) => {
    const a = rowA?.values[columnId]?.toLowerCase() ?? '';
    const b = rowB?.values[columnId]?.toLowerCase() ?? '';
    if (a > b) {
      return 1;
    }
    if (a < b) {
      return -1;
    }
    return 0;
  },

  statusSort: (rowA, rowB, columnId) => {
    const a = rowA?.values[columnId];
    const b = rowB?.values[columnId];
    if (a > b) {
      return 1;
    }
    if (a < b) {
      return -1;
    }
    return 0;
  },

  getOrCreateDeviceId: async () => {
    try {
      // First check localStorage for existing DEVICE_ID
      let storedDeviceId = localStorage.getItem('DEVICE_ID');

      if (storedDeviceId) {
        return storedDeviceId;
      }

      // Create a ClientJS instance
      const client = new ClientJs.ClientJS();

      // Get the visitor identifier
      const fingerprint = client.getFingerprint();

      // Create a composite ID using fingerprint + UUID for uniqueness
      storedDeviceId = `${fingerprint}-${uuidv4()}-${Date.now()}`;
      // Store the generated device ID in localStorage
      localStorage.setItem('DEVICE_ID', storedDeviceId);

      return storedDeviceId;
    } catch {
      // Fallback to random UUID if fingerprinting fails
      const fallbackId = `${uuidv4()}-${Date.now()}`;
      localStorage.setItem('DEVICE_ID', fallbackId);
      return fallbackId;
    }
  },

  updateUrlParams: (url, params = {}, removeParams = []) => {
    /**
     * Updates the URL with new query parameters and removes specified ones.
     * @param {string | URL} url - The URL to update, can be a string or a URL object.
     * @param {Object} params - An object containing key-value pairs of parameters to add or update.
     * @param {Array<string>} removeParams - An array of parameter names to remove from the URL.
     * @returns {string} The updated URL as a string.
     */
    const urlObj = url instanceof URL ? url : new URL(url);
    removeParams.forEach((param) => urlObj.searchParams.delete(param));
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        urlObj.searchParams.set(key, value);
      }
    });
    return urlObj.toString();
  },
};

export default utils;
